import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { CarModelService } from 'src/app/services/car-model/car-model.service';
import { CarModelModalService } from 'src/app/services/car-model-modal/car-model-modal.service';
import Swal from 'sweetalert2';
import { forkJoin } from 'rxjs';

declare var bootstrap: any;

@Component({
  selector: 'app-models',
  templateUrl: './models.component.html',
  styleUrls: ['./models.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class ModelsComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  carModels: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedCarModels: string[] = [];

  constructor(
    private _carModelService: CarModelService,
    private _carModelModalService: CarModelModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadCarModels();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  private loadCarModels(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    this.subscriptions.push(
      this._carModelService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.carModels = response.data.data || [];
            this.paginationInfo = response.data.pagination || this.paginationInfo;
          } else {
            this.carModels = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error: any) => {
          console.error('Error loading car models:', error);
          this.carModels = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadCarModels(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadCarModels(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadCarModels();
  }

  openAddCarModelModal(): void {
    this._carModelModalService.clearSelectedCarModel();
    const modalElement = document.getElementById('kt_modal_car_model_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(carModel: any): void {
    this._carModelModalService.setSelectedCarModel(carModel);
    const modalElement = document.getElementById('kt_modal_car_model_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteCarModel(carModel: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${carModel.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(carModel);
      }
    });
  }

  private performDelete(carModel: any): void {
    const carModelId = carModel._id || carModel.id;
    this.subscriptions.push(
      this._carModelService.DeleteDataById(carModelId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Car model has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadCarModels(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete car model.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting car model.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onCarModelAdded(): void {
    console.log('Car model added, refreshing list...');
    this.loadCarModels(this.currentPage, this.searchTerm, this.filters);
  }

  onCarModelUpdated(): void {
    console.log('Car model updated, refreshing list...');
    this.loadCarModels(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Selection methods
  isSelected(carModelId: string): boolean {
    return this.selectedCarModels.includes(carModelId);
  }

  isAllSelected(): boolean {
    return this.carModels.length > 0 && this.selectedCarModels.length === this.carModels.length;
  }

  isIndeterminate(): boolean {
    return this.selectedCarModels.length > 0 && this.selectedCarModels.length < this.carModels.length;
  }

  toggleSelection(carModelId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedCarModels.includes(carModelId)) {
        this.selectedCarModels.push(carModelId);
      }
    } else {
      this.selectedCarModels = this.selectedCarModels.filter(id => id !== carModelId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedCarModels = this.carModels.map(carModel => carModel._id || carModel.id);
    } else {
      this.selectedCarModels = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-car-model-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-car-model-table-toolbar="selected"]');

    if (this.selectedCarModels.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }



  bulkDelete(): void {
    if (this.selectedCarModels.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${this.selectedCarModels.length} car model(s). This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedCarModels.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._carModelService.BulkDelete(this.selectedCarModels).subscribe({
        next: () => {
          this.loadCarModels();
          this.selectedCarModels = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected car models have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting car models:', error);
          Swal.fire('Error!', 'Failed to delete selected car models.', 'error');
        }
      })
    );
  }

  // Utility for template
  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  // Math utility for template
  Math = Math;
}
